import React, { useState, useEffect } from "react";
import "./Product.css";
import { useStateValue } from "../../StateProvider";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Eye, Plus, Minus, ShoppingCart } from "lucide-react";
import { tablesDB, DB_ID, PRODUCTS_TABLE } from "../../appwrite";

function slugify(text) {
  return text
    ? text
        .toString()
        .toLowerCase()
        .replace(/[^\w\s]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+$/g, "")
    : "";
}

function Product({
  product_id,
  productImage,
  productName,
  price,
  productDescription,
  category,
  showCategory = true,
}) {
  const [{ cart }, dispatch] = useStateValue();
  const [isAdded, setIsAdded] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [available, setAvailable] = useState(true);
  const [sold, setSold] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const MAX_QTY = 3;

  const isReup = category === "reup-vault";

  useEffect(() => {
    let cancelled = false;
    tablesDB
      .getRow({ databaseId: DB_ID, tableId: PRODUCTS_TABLE, rowId: product_id })
      .then((data) => {
        if (cancelled) return;
        setAvailable(data.available);
        setSold(data.sold || false);
        if ((data.sold || !data.available) && isReup) {
          const inCart = cart.find((i) => i.id === product_id);
          if (inCart) {
            dispatch({ type: "REMOVE_FROM_CART", id: product_id });
            setIsAdded(false);
            toast.warn(`${productName} has been sold.`, { autoClose: 2000 });
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [product_id]);

  useEffect(() => {
    const inCart = cart.find((i) => i.id === product_id);
    setIsAdded(!!inCart);
    if (inCart) setQuantity(inCart.quantity);
  }, [cart, product_id]);

  const formatPrice = (p) =>
    parseFloat(String(p).replace(/,/g, "")).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const addToCart = () => {
    dispatch({
      type: "ADD_TO_CART",
      item: {
        id: product_id,
        title: productName,
        image: productImage,
        price: price,
        category,
        available,
      },
    });
    toast.success(`Added to cart!`, { autoClose: 1800 });
    setIsAdded(true);
  };

  const updateQty = (n) => {
    if (n < 1) {
      dispatch({ type: "REMOVE_FROM_CART", id: product_id });
      setIsAdded(false);
    } else if (n <= MAX_QTY) {
      dispatch({ type: "UPDATE_QUANTITY", id: product_id, quantity: n });
      setQuantity(n);
    } else toast.warn(`Max ${MAX_QTY} per item`, { autoClose: 1500 });
  };

  const isSoldOut = sold || !available;

  return (
    <div
      className={`pc ${isSoldOut ? "pc--sold" : ""}`}
      onClick={() => navigate(`/product/${slugify(productName)}`)}
    >
      <div className="pc__img-wrap">
        <img
          src={productImage}
          alt={productName}
          className="pc__img"
          loading="lazy"
        />
        {showCategory && category && (
          <span className="pc__badge">{category.replace(/-/g, " ")}</span>
        )}
        {isSoldOut && (
          <div className="pc__sold-overlay">
            <span>{sold ? "SOLD" : "Out of Stock"}</span>
          </div>
        )}
      </div>
      <div className="pc__body">
        <p className="pc__name">{productName}</p>
        <p className="pc__price">₦{formatPrice(price)}</p>
        <div className="pc__actions">
          {isReup ? (
            <button
              className="pc__btn pc__btn--view"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/product/${slugify(productName)}`);
              }}
            >
              <Eye size={15} /> View
            </button>
          ) : isSoldOut ? (
            <button className="pc__btn pc__btn--oos" disabled>
              Out of Stock
            </button>
          ) : isAdded ? (
            <div className="pc__stepper" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => updateQty(quantity - 1)}>
                <Minus size={13} />
              </button>
              <span>{quantity}</span>
              <button
                onClick={() => updateQty(quantity + 1)}
                disabled={quantity >= MAX_QTY}
              >
                <Plus size={13} />
              </button>
            </div>
          ) : (
            <button
              className="pc__btn pc__btn--add"
              onClick={(e) => {
                e.stopPropagation();
                addToCart();
              }}
            >
              <ShoppingCart size={15} /> Add to Cart
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Product;
